import { LIBRARY_KEYS, loadLibraries } from "@corca/data/libraries";
import { itemSymbolNode, libraryItemType } from "@corca/data/libraries/item";
import { sortBy } from "es-toolkit";
import { computed, makeAutoObservable, observable, runInAction } from "mobx";

import type { Library, LibraryItem } from "@corca/data";
import type { LibraryItemType } from "@corca/data/libraries/item";

type Status = "idle" | "loading" | "ready" | "failed";

/**
 * Where the libraries for a set of keys come from: the bundle today, the API with the bundle as
 * fallback once it exists (plan, Loading).
 */
export type LibrarySource = (keys: string[]) => Promise<Library[]>;

const bundleSource: LibrarySource = () => loadLibraries();

/**
 * The source files whose items the libraries serve instead of the JSON provider of each; a file
 * is added here when its items are ready to take over.
 */
export const MIGRATED_SOURCES = new Set([
  "expressions.json",
  "functions.json",
  "math-variables.json",
  "physics-variables.json",
  "constants.json",
  "symbols.json",
  "operator-symbols.json",
  "supplemental-operator-symbols.json",
]);

/**
 * How a model starts: on or off, and with the bundle already loaded when `libraries` is given,
 * so nothing waits for a load.
 */
export type LibrariesStart = { enabled?: boolean; libraries?: Library[] };

let documentStart: LibrariesStart = {};

/**
 * How every document's model starts from now on. The jest setup turns core libraries on for the
 * whole suite with `CORE_LIBRARIES=1`, which is how a migration is checked against every test.
 */
export function setDocumentLibrariesStart(start: LibrariesStart) {
  documentStart = start;
}

export function documentLibrariesStart(): LibrariesStart {
  return documentStart;
}

/**
 * Which libraries this document draws suggestions from, and whether it does so yet. `enabled`
 * is off by default and the libraries panel turns it on for the session; the first time it does,
 * the core set is loaded from the bundle. `enabledKeys` is the core set until the document record
 * carries its own choice (plan, Enablement).
 */
export class LibrariesModel {
  enabled = false;

  enabledKeys: string[] = [...LIBRARY_KEYS];

  libraries = new Map<string, Library>();

  status: Status = "idle";

  private readonly load: LibrarySource;

  constructor(load: LibrarySource = bundleSource, start: LibrariesStart = {}) {
    this.load = load;
    this.enabled = start.enabled ?? false;

    if (start.libraries) {
      this.libraries = new Map(start.libraries.map((library) => [library.key, library]));
      this.status = "ready";
    }

    makeAutoObservable<this, "load">(this, {
      libraries: observable.ref,
      load: false,
      items: computed({ keepAlive: true }),
      itemsByType: computed({ keepAlive: true }),
      servedItems: computed({ keepAlive: true }),
      knownItems: computed({ keepAlive: true }),
      itemsBySymbolKey: computed({ keepAlive: true }),
      symbolsByGlyph: computed({ keepAlive: true }),
    });

    if (this.enabled) this.ensureLoaded();
  }

  /**
   * Kept alive, so reading it outside a reaction returns the cached array
   * rather than a new one each time, and readers can cache by it.
   */
  get items(): LibraryItem[] {
    const seen = new Set<string>();

    return this.enabledKeys.flatMap((key) => {
      const items = this.libraries.get(key)?.items ?? [];

      return items.filter((item) => {
        if (seen.has(item.key)) {
          return false;
        }

        seen.add(item.key);
        return true;
      });
    });
  }

  /**
   * The items grouped by `libraryItemType`, kept alive like `items` so readers can cache by it.
   */
  get itemsByType(): Map<LibraryItemType, LibraryItem[]> {
    const byType = new Map<LibraryItemType, LibraryItem[]>();

    for (const item of this.items) {
      const type = libraryItemType(item);
      const group = byType.get(type);

      if (group) {
        group.push(item);
      } else {
        byType.set(type, [item]);
      }
    }

    return byType;
  }

  /**
   * The items whose source file the libraries serve instead of its JSON provider, kept alive
   * like `items` so readers can cache by it.
   */
  get servedItems(): LibraryItem[] {
    return this.items.filter((item) => this.serves(item.__source));
  }

  /**
   * The served items of every loaded library, whether or not its key is enabled: what the
   * libraries know, as opposed to what they offer. Disabling a library hides its rows; it does
   * not make a node already in the document unknown.
   */
  get knownItems(): LibraryItem[] {
    const seen = new Set<string>();
    const known: LibraryItem[] = [];

    for (const library of this.libraries.values()) {
      for (const item of library.items) {
        if (seen.has(item.key) || !this.serves(item.__source)) continue;
        seen.add(item.key);
        known.push(item);
      }
    }

    return known;
  }

  /**
   * The known items by symbol key, the symbol item first: a constant such as the Euler–Mascheroni
   * γ carries the Greek letter's key too, while a key only a constant carries, such as Euler's
   * number, resolves to that constant.
   */
  get itemsBySymbolKey(): Map<string, LibraryItem> {
    const byKey = new Map<string, LibraryItem>();
    const symbolsFirst = sortBy(this.knownItems, [(item) => (item.suggestion?.isSymbol ? 0 : 1)]);

    for (const item of symbolsFirst) {
      const key = itemSymbolNode(item)?.props.key;
      if (key && !byKey.has(key)) byKey.set(key, item);
    }

    return byKey;
  }

  itemBySymbolKey(symbolKey: string): LibraryItem | undefined {
    return this.itemsBySymbolKey.get(symbolKey);
  }

  /**
   * The known symbol items, the ones the suggestions grid shows, by their glyph; first one wins.
   */
  get symbolsByGlyph(): Map<string, LibraryItem> {
    const byGlyph = new Map<string, LibraryItem>();

    for (const item of this.knownItems) {
      const glyph = itemSymbolNode(item)?.props.value;
      if (item.suggestion?.isSymbol && glyph && !byGlyph.has(glyph)) byGlyph.set(glyph, item);
    }

    return byGlyph;
  }

  symbolByGlyph(glyph: string): LibraryItem | undefined {
    return this.symbolsByGlyph.get(glyph);
  }

  toggle() {
    this.enabled = !this.enabled;
    if (this.enabled) this.ensureLoaded();
  }

  /**
   * Whether the items generated from a source file come from the libraries rather than its JSON
   * provider: only while the libraries are on and loaded, so a search during the load still has
   * the file's items.
   */
  serves(source: string | undefined): boolean {
    return (
      this.enabled &&
      this.status === "ready" &&
      source !== undefined &&
      MIGRATED_SOURCES.has(source)
    );
  }

  isKeyEnabled(key: string): boolean {
    return this.enabledKeys.includes(key);
  }

  setKeyEnabled(key: string, on: boolean) {
    if (on === this.isKeyEnabled(key)) return;

    const enabledKeys = on
      ? [...this.enabledKeys, key]
      : this.enabledKeys.filter((enabled) => enabled !== key);

    this.enabledKeys = enabledKeys;
  }

  setAllKeys(on: boolean) {
    this.enabledKeys = on ? [...LIBRARY_KEYS] : [];
  }

  retry() {
    if (this.status === "failed") this.loadBundle();
  }

  private ensureLoaded() {
    if (this.status === "idle" || this.status === "failed") this.loadBundle();
  }

  private async loadBundle() {
    this.status = "loading";

    try {
      const libraries = await this.load(this.enabledKeys);

      runInAction(() => {
        this.libraries = new Map(libraries.map((library) => [library.key, library]));
        this.status = "ready";
      });
    } catch (error) {
      console.error("Failed to load the core libraries", error);

      runInAction(() => {
        this.status = "failed";
      });
    }
  }
}
